import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import useStore from '../store'
import GoalForm from '../components/goals/GoalForm'
import PhaseList from '../components/phases/PhaseList'
import { goalFlags, inactionMsg } from '../lib/constraints'
import { fmtWeeks } from '../lib/timeline'
import { CONFIG } from '../config/config'

export default function GoalDetailPage() {
  const { id }       = useParams()
  const navigate     = useNavigate()
  const goals        = useStore(s => s.goals)
  const deleteGoal   = useStore(s => s.deleteGoal)
  const afterMutation = useStore(s => s.afterMutation)
  const profile      = useStore(s => s.profile)

  const [editing,    setEditing]    = useState(false)
  const [confirming, setConfirming] = useState(false)

  const goal    = goals.find(g => g.id === id)
  if (!goal) return (
    <div className="p-5 flex items-center gap-2">
      <Link to="/goals" className="font-mono text-xs text-m-faint hover:text-m-accent">← Goals</Link>
      <span className="font-mono text-xs text-m-faint">/ Not found</span>
    </div>
  )

  const flags   = goalFlags(goal, profile?.weekly_available_hours)
  const inaction = inactionMsg(goal, profile?.weekly_available_hours)
  const totalH  = (goal.estimated_weeks ?? 0) * (goal.weekly_hours ?? 0)

  const handleDelete = async () => {
    await deleteGoal(goal.id)
    afterMutation()
    navigate('/goals', { replace: true })
  }

  return (
    <motion.div
      className="h-full overflow-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
    >
      <div className="max-w-2xl mx-auto px-5 py-5">

        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 mb-5 font-mono text-[10px] text-m-faint">
          <Link to="/goals" className="hover:text-m-accent transition-colors">Goals</Link>
          <span>/</span>
          <span className="text-m-subtle truncate">{goal.title}</span>
        </div>

        {/* Goal header card */}
        <div className="card p-5 mb-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-start gap-2.5 min-w-0">
              {goal.color && <div className="w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{ backgroundColor: goal.color }} />}
              <div>
                <h1 className="font-mono text-lg text-m-text leading-tight">{goal.title}</h1>
                {goal.category && <p className="font-mono text-[11px] text-m-faint mt-0.5">{goal.category}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`font-mono text-[10px] ${goal.status === 'active' ? 'text-m-accent' : goal.status === 'paused' ? 'text-m-warn' : 'text-m-faint'}`}>
                {goal.status}
              </span>
              <button onClick={() => setEditing(e => !e)}
                className="font-mono text-[10px] text-m-faint hover:text-m-subtle">
                {editing ? 'close' : 'edit'}
              </button>
            </div>
          </div>

          {goal.description && (
            <p className="font-mono text-sm text-m-subtle leading-relaxed mb-3">{goal.description}</p>
          )}

          {/* Metrics grid */}
          <div className="grid grid-cols-4 gap-3 mb-3">
            <div>
              <p className="label">Load</p>
              <span className={`tag mt-1 inline-block ${goal.effort_level === 1 ? 'tag-low' : goal.effort_level === 2 ? 'tag-medium' : 'tag-high'}`}>
                {CONFIG.EFFORT[goal.effort_level]}
              </span>
            </div>
            <div><p className="label">Duration</p><p className="font-mono text-sm text-m-text mt-1">{fmtWeeks(goal.estimated_weeks)}</p></div>
            <div><p className="label">Pace</p><p className="font-mono text-sm text-m-text mt-1">{goal.weekly_hours ? `${goal.weekly_hours}h/wk` : '—'}</p></div>
            <div><p className="label">Total effort</p><p className="font-mono text-sm text-m-text mt-1">{totalH > 0 ? `~${totalH}h` : '—'}</p></div>
          </div>

          {/* Flags */}
          {flags.map((fl, i) => <div key={i} className="alert-warn mb-2">{fl.msg}</div>)}
          {inaction && <div className="alert-info mb-2">{inaction.msg}</div>}

          {/* Edit form */}
          <AnimatePresence>
            {editing && (
              <motion.div
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }} className="overflow-hidden"
              >
                <div className="border-t border-m-border pt-4 mt-3">
                  <GoalForm existing={goal} onDone={() => setEditing(false)} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Phases */}
        <div className="card p-5 mb-4">
          <PhaseList goalId={goal.id} />
        </div>

        {/* Danger zone */}
        <div className="card p-4 border-m-danger/20">
          {confirming ? (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-xs text-m-danger">Delete permanently?</span>
              <button onClick={handleDelete} className="btn-danger btn-sm">Confirm</button>
              <button onClick={() => setConfirming(false)} className="btn-ghost btn-sm">Cancel</button>
            </div>
          ) : (
            <button onClick={() => setConfirming(true)}
              className="font-mono text-[11px] text-m-faint hover:text-m-danger transition-colors">
              Delete this goal
            </button>
          )}
        </div>
      </div>
    </motion.div>
  )
}
